/**
 * Global error reporter — posts frontend crashes and interceptor
 * failures to the backend so we can see what actually broke on a user's
 * browser instead of guessing from a screenshot. This is the poor-man's
 * Sentry: no external service, results land in MongoDB and are visible
 * at GET /api/client-errors/recent.
 *
 * Kept dependency-free (no axios) so it can report failures happening
 * inside axios itself without recursion. Uses navigator.sendBeacon when
 * available so reports survive an in-progress page unload.
 */

const ENDPOINT = `${process.env.REACT_APP_BACKEND_URL}/api/client-errors`;

// De-dupe: don't send the same fingerprint more than once per minute.
// Prevents runaway loops (e.g. a broken render that re-throws on every
// re-mount) from flooding the backend.
const _recent = new Map(); // fingerprint -> timestamp
const _DEDUPE_MS = 60_000;

function _fingerprint(payload) {
  try {
    return `${payload.kind || ""}:${(payload.message || "").slice(0, 200)}:${(payload.stack || "").split("\n")[1] || ""}`;
  } catch {
    return String(Math.random());
  }
}

function _dedupe(payload) {
  const fp = _fingerprint(payload);
  const now = Date.now();
  const last = _recent.get(fp);
  if (last && now - last < _DEDUPE_MS) return true;
  _recent.set(fp, now);
  // Cap the map so it doesn't grow forever
  if (_recent.size > 200) {
    const oldest = [..._recent.entries()].sort((a, b) => a[1] - b[1])[0];
    if (oldest) _recent.delete(oldest[0]);
  }
  return false;
}

export function reportClientError(payload) {
  try {
    const enriched = {
      ...payload,
      url: typeof window !== "undefined" ? window.location.href : "",
      timestamp: new Date().toISOString(),
      user_id: (() => {
        try {
          const token = localStorage.getItem("access_token");
          if (!token) return null;
          // Decode JWT payload without verifying — just to identify the
          // user in triage. Never sent as auth.
          const seg = token.split(".")[1];
          if (!seg) return null;
          return JSON.parse(atob(seg.replace(/-/g, "+").replace(/_/g, "/")))?.sub || null;
        } catch {
          return null;
        }
      })(),
    };
    if (_dedupe(enriched)) return;

    const body = JSON.stringify(enriched);
    // Prefer sendBeacon so the report survives page unloads / reloads —
    // exactly the scenario in the "dashboard flashes then reloads"
    // bug where a synchronous fetch would be cancelled.
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(ENDPOINT, blob);
      return;
    }
    // Fallback: fire-and-forget fetch, keepalive so a following reload
    // doesn't abort it. Never awaited — we don't want the reporter to
    // block the user's flow.
    fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => { /* swallow — reporter must never crash the app */ });
  } catch {
    // If the reporter itself throws we have no recourse — swallow.
  }
}

// Wire browser-level listeners once at import time. Any thrown Error
// that isn't caught by a component's ErrorBoundary will surface here.
if (typeof window !== "undefined" && !window.__clientErrorReporterWired) {
  window.__clientErrorReporterWired = true;

  window.addEventListener("error", (event) => {
    reportClientError({
      kind: "window.error",
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      stack: event.error?.stack || "",
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    reportClientError({
      kind: "unhandled_rejection",
      message: (reason && (reason.message || String(reason))) || "unknown",
      stack: (reason && reason.stack) || "",
    });
  });
}
